import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PublicHeader } from './PublicHeader';
import { PublicFooter } from './PublicFooter';
import { StickyCallBar } from './StickyCallBar';
import { ChatbotConsentLauncher } from '@/components/public/ChatbotConsentLauncher';
import { useRestaurantSettings } from '@/features/restaurant/useRestaurantSettings';
import { toOpeningHoursSpecification } from '@sizilianer/core/features/restaurant/settings';

/**
 * www ist die kanonische Domain (Apex leitet mit 308 auf www).
 *
 * Exportiert, weil /la-putia sein eigenes Store-JSON-LD baut und dieselbe
 * Domain nennen muss — dort standen bis 01.08. zwei hartkodierte Adressen
 * OHNE www, was die eigene Prüfregel SDA-04 als Fehler meldete.
 * `tests/unit/seoUrls.test.ts` hält diesen Wert gegen CANONICAL_ORIGIN aus
 * api/_lib/seo/publicUrls.ts, damit die beiden nie auseinanderlaufen.
 */
export const SITE_URL = 'https://www.die-sizilianer.de';

/** Routen, die das Restaurant-JSON-LD tragen (10.SD1: genau EIN
    LocalBusiness je Seite; /la-putia hat seine eigene Entity). */
const SCHEMA_ROUTES = new Set(['/', '/kontakt', '/reservieren', '/speisekarte']);

type PublicPageTemplateProps = {
  /** Page-Title — wird zu "Sub | Die Sizilianer Dorsten" (10.OP1: Ort im Title) */
  pageTitle?: string;
  /** Optional Meta-Description */
  description?: string;
  /** Page-Content (zwischen Header und Footer) */
  children: ReactNode;
};

/**
 * Standard-Layout für alle Public-Pages.
 *
 * - PublicHeader (sticky) + Tricolore, Skip-Link, StickyCallBar
 * - SEO je Route: Title (mit Ort), Meta-Description, self-Canonical,
 *   Restaurant-JSON-LD mit Öffnungszeiten aus dem Backoffice.
 *   (OG-Tags bleiben statisch in index.html — Social-Scraper rendern kein JS.)
 */
export function PublicPageTemplate({
  pageTitle,
  description,
  children,
}: PublicPageTemplateProps) {
  const location = useLocation();
  const { data: settings } = useRestaurantSettings();

  const fullTitle = pageTitle
    ? `${pageTitle} | Die Sizilianer Dorsten`
    : 'Die Sizilianer — Sizilianische Spezialitäten in Dorsten';

  // Canonical ohne Query/Hash, ohne Trailing-Slash-Varianten (21.A12)
  const canonical =
    SITE_URL + (location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, ''));

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  /**
   * Canonical imperativ statt per React-19-Hoisting.
   *
   * WARUM NICHT `<link rel="canonical">` im JSX: React 19 hoistet zwar
   * zuverlässig in den <head>, dedupliziert dabei aber NICHT. `mountHoistable`
   * fügt bedingungslos ein; nur echte Resources (Stylesheets, Preloads) laufen
   * über einen DOM-Abgleich — ein Canonical ist ein Hoistable, keine Resource.
   *
   * Solange die ausgelieferte index.html gar keinen Canonical enthält, fällt
   * das nicht auf. Sobald aber prerenderte Seiten einen mitbringen (er steht
   * im <head>, also AUSSERHALB #root, und überlebt den Mount), hinge React
   * einen zweiten daneben — die Seite nennte zwei kanonische Adressen, und
   * genau das meldet die eigene Prüfregel MTA-13 als Fehler.
   *
   * Dieselbe Form wie die Description direkt darunter: vorhandenes Element
   * aktualisieren, nur bei Fehlen neu anlegen. Kein Cleanup — jede Route setzt
   * ihren eigenen Wert, und ein zurückgesetzter Canonical wäre schlechter als
   * ein kurz veralteter.
   */
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }, [canonical]);

  // Meta-Description (per useEffect — DOM-Update)
  useEffect(() => {
    if (!description) return;
    let metaTag = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    const previous = metaTag.content;
    metaTag.content = description;
    return () => {
      if (metaTag) metaTag.content = previous;
    };
  }, [description]);

  // Restaurant-Schema — Öffnungszeiten live aus restaurant_settings,
  // damit Schema und sichtbare Zeiten nie divergieren (10.SD1 / Audit #49).
  const showSchema = SCHEMA_ROUTES.has(location.pathname);
  // KEIN Fallback mehr. Bis zum 01.08. standen hier Ersatzzeiten im Code, die
  // Montag bis 23:00 behaupteten — in Wahrheit ist um 22:00 Schluss. Solange
  // die echten Daten nicht da sind, sagt das Schema über Öffnungszeiten
  // NICHTS: eine falsche Stunde im Google-Wissenspanel ist teurer als eine
  // fehlende Angabe. Der Prerender wartet ohnehin auf die geladenen Daten.
  const openingHours = toOpeningHoursSpecification(settings?.opening_hours_jsonb);

  const schema = showSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'Restaurant',
        name: settings?.name ?? 'Die Sizilianer',
        description:
          'Authentische sizilianische Küche in Dorsten. Reservierung, Events, Speisekarte.',
        url: SITE_URL,
        telephone:
          '+49' + (settings?.phone ?? '02362 23279').replace(/\s/g, '').replace(/^0/, ''),
        email: settings?.email ?? 'info@die-sizilianer.de',
        servesCuisine: ['Italian', 'Sicilian', 'Mediterranean'],
        priceRange: '€€',
        image: `${SITE_URL}/assets/Logo%20Sizilianer.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: settings?.address_street ?? 'Im Werth 25',
          postalCode: settings?.address_zip ?? '46282',
          addressLocality: settings?.address_city ?? 'Dorsten',
          addressRegion: 'NRW',
          addressCountry: 'DE',
        },
        geo: { '@type': 'GeoCoordinates', latitude: 51.66520, longitude: 6.96183 },
        // Leere Liste weglassen statt als [] ausgeben — ein leeres Pflichtfeld
        // ist für einen Auswerter dasselbe wie ein fehlendes, sieht aber aus,
        // als sei die Frage beantwortet.
        ...(openingHours.length > 0 ? { openingHoursSpecification: openingHours } : {}),
        sameAs: [
          'https://facebook.com/diesizilianer',
          'https://instagram.com/diesizilianer',
          // Das Google-Unternehmensprofil gehört hier ebenfalls hinein — es ist
          // die Verbindung zwischen Website und Karteneintrag. Es fehlt noch,
          // weil die Place-ID (aus GOOGLE_REVIEW_URL) nicht verifiziert ist:
          // Google Maps liefert nur eine JS-Hülle, die Zuordnung lässt sich von
          // hier aus nicht belegen. Eine unbelegte Identitätsaussage im Schema
          // wäre schlimmer als eine fehlende.
        ],
        acceptsReservations: `${SITE_URL}/reservieren`,
        // Die Speisekarte maschinenlesbar verknüpfen. Für ein Restaurant eine
        // der wenigen Auszeichnungen, die Google direkt im Treffer anzeigt.
        hasMenu: `${SITE_URL}/speisekarte`,
        // Ermöglicht einen „Tisch reservieren"-Knopf direkt im Suchergebnis.
        // `target` als EntryPoint, weil eine blanke URL für Google keine
        // Handlung ist, sondern nur ein Verweis.
        potentialAction: {
          '@type': 'ReserveAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/reservieren`,
            inLanguage: 'de-DE',
            actionPlatform: [
              'http://schema.org/DesktopWebPlatform',
              'http://schema.org/MobileWebPlatform',
            ],
          },
          result: { '@type': 'FoodEstablishmentReservation', name: 'Tischreservierung' },
        },
      }
    : null;

  return (
    /* data-settings: das einzige verlässliche DOM-Signal dafür, dass
       restaurant_settings geladen sind. Der Prerender darf nicht früher
       schnappschießen — sonst stünde im JSON-LD gar keine Öffnungszeit, und
       genau die ist die Angabe, die Google im Wissenspanel zeigt.
       (Bis 01.08. stand hier stattdessen ein Ersatzwert im Code, der Montag
       bis 23:00 behauptete — der Fehler war also nicht „nichts", sondern
       „etwas Falsches". Der Ersatzwert ist weg, das Warten bleibt.)
       IDX-04 fängt beides nicht, weil Titel und Canonical dabei stimmen.
       Gelesen von tests/prerender/prerender.spec.ts. */
    <div className="page" data-settings={settings ? 'ready' : 'loading'}>
      {/* Canonical wird oben imperativ gesetzt — NICHT hier als JSX, sonst
          entstehen nach dem Prerender zwei davon (Begründung am useEffect). */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}

      {/* WCAG 2.4.1: Skip-Link als erstes fokussierbares Element —
          unsichtbar bis Tastatur-Fokus, springt direkt zum Inhalt. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-paper focus:text-ink-2 focus:border focus:border-gold-2 focus:rounded focus:px-4 focus:py-2.5 focus:font-sans focus:text-sm focus:font-semibold focus:shadow-md"
      >
        Zum Inhalt springen
      </a>
      <PublicHeader />
      <main id="main" tabIndex={-1} className="outline-none">
        {children}
      </main>
      <PublicFooter />
      <StickyCallBar />
      <ChatbotConsentLauncher />
    </div>
  );
}
